import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Collapse, TableCell, TableRow, Typography, useTheme, Divider } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { CollapsedItem } from './collapsed-item.component'
import { ImpactRecord } from './impact-record.component'
import { OuterLink } from './outer-link.component'
import { MediaList } from './media.component'
import { ScanMap } from './map.component'
import { EScanStatus, ICoords, IStepLocation } from 'src/types'
import { Loader } from 'src/components/loader'

import { COORDS_MULTIPLIER, WS_TRANSACTION_STATUS } from 'src/constants'
import {
  capitalizeFirstLetter,
  findLocationsWithinGeofence,
  getBoundsIncludingRadii,
  getScanExplorerTxUrl,
  getTableTimestamp,
} from 'src/common/utils'
import { ETranslationNs, IScanRespDto } from 'src/types'
import { useStyles } from './styles'
import { useAccountTransactionStatus, useUserTransactionStatus } from 'src/hooks'

interface IProps {
  collapsed: boolean
  item: IScanRespDto
  userTz?: string
}

export const BodyRowCollapse = ({ collapsed, item, userTz }: IProps) => {
  const [t] = useTranslation([ETranslationNs.ScanForm, ETranslationNs.Scans])
  const classes = useStyles()
  const theme = useTheme()

  const mapRef = useRef<google.maps.Map | null>(null)

  const userTransaction = useUserTransactionStatus(item.id)
  const [userTxLoader, setUserTxLoader] = useState(
    userTransaction.status === WS_TRANSACTION_STATUS.PENDING,
  )

  const accountTransaction = useAccountTransactionStatus(item.id)
  const [accountTxLoader, setAccountTxLoader] = useState(
    accountTransaction.status === WS_TRANSACTION_STATUS.PENDING,
  )

  useEffect(() => {
    if (userTransaction.status === WS_TRANSACTION_STATUS.PENDING) {
      setUserTxLoader(true)
    } else {
      setUserTxLoader(false)
    }
  }, [userTransaction.status])
  useEffect(() => {
    if (accountTransaction.status === WS_TRANSACTION_STATUS.PENDING) {
      setAccountTxLoader(true)
    } else {
      setAccountTxLoader(false)
    }
  }, [accountTransaction.status])

  const stepConnectedAccount = item?.step?.account_connection

  const stepLocations: IStepLocation[] = useMemo(() => {
    return item.step?.locations
      ? item.step.locations.map((location) => ({
          lat: location.latitude / COORDS_MULTIPLIER,
          lng: location.longitude / COORDS_MULTIPLIER,
          radius: location.radius,
          name: item.step?.name,
          location_name: location.location_name,
          connected_account_name: stepConnectedAccount?.account_approved
            ? stepConnectedAccount?.account?.account_holder
            : null,
          connected_account_img:
            stepConnectedAccount?.account.image_small ||
            stepConnectedAccount?.account.image_medium ||
            null,
        }))
      : []
  }, [item.step?.locations, stepConnectedAccount])

  const scannerLocation = useMemo(
    () => ({
      lat: item.scanner_latitude / COORDS_MULTIPLIER,
      lng: item.scanner_longitude / COORDS_MULTIPLIER,
    }),
    [item.scanner_latitude, item.scanner_longitude],
  )

  const center = useMemo(() => {
    const allLocations = [
      scannerLocation,
      ...stepLocations.map((loc) => ({ lat: loc.lat, lng: loc.lng })),
    ]

    const latitudes = allLocations.map((loc) => loc.lat)
    const longitudes = allLocations.map((loc) => loc.lng)

    const minLat = Math.min(...latitudes)
    const maxLat = Math.max(...latitudes)
    const minLng = Math.min(...longitudes)
    const maxLng = Math.max(...longitudes)

    return {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    }
  }, [scannerLocation, stepLocations])

  const handleCenter = useCallback(() => {
    if (!mapRef?.current) return

    const locationsWithinGeofence = findLocationsWithinGeofence(stepLocations, scannerLocation)

    const locationsWithRadius = locationsWithinGeofence.map((step) => ({
      lat: step.lat,
      lng: step.lng,
      radius: step.radius || 0,
    }))

    const bounds = getBoundsIncludingRadii(locationsWithRadius)

    mapRef.current.fitBounds(bounds)

    const zoomLevelAdjustment = 1
    const map = mapRef.current

    if (map) {
      google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        const currentZoom = map.getZoom()
        if (currentZoom) {
          map.setZoom(currentZoom + zoomLevelAdjustment)
        }
      })
    }
  }, [item.scanner_latitude, item.scanner_longitude, item.step_radius, stepLocations])

  return (
    <TableRow className={classes.row}>
      <TableCell colSpan={9} className={classes.cell}>
        <Collapse in={collapsed} timeout="auto" unmountOnExit>
          <ul className={classes.collapsedContent}>
            {item?.batch_id && (
              <CollapsedItem
                title={t('batch-id-table-column.v', { ns: ETranslationNs.Scans })}
                node={<Typography sx={{ fontSize: '14px' }}>{item.batch_id}</Typography>}
              />
            )}
            <CollapsedItem
              title={t('scan-form-record-type-label.v')}
              node={
                <Typography sx={{ fontSize: '14px' }}>
                  {capitalizeFirstLetter(item.record_type)}
                </Typography>
              }
            />
            {item?.sender_reference_id && (
              <CollapsedItem
                title={t('scan-form-s-reference-input-label.v')}
                node={<Typography sx={{ fontSize: '14px' }}>{item.sender_reference_id}</Typography>}
              />
            )}
            {item?.receiver_reference_id && (
              <CollapsedItem
                title={t('scan-form-r-reference-input-label.v')}
                node={
                  <Typography sx={{ fontSize: '14px' }}>{item.receiver_reference_id}</Typography>
                }
              />
            )}
            {item?.invoice_reference && (
              <CollapsedItem
                title={t('scan-form-invoice-reference-input-label.v')}
                node={<Typography sx={{ fontSize: '14px' }}>{item.invoice_reference}</Typography>}
              />
            )}
            {item?.impact && (
              <CollapsedItem
                title={t('scan-form-impact-checkbox.v')}
                node={
                  <ImpactRecord
                    impact={item.impact}
                    quantity={item.impact_quantity}
                    quantity_type={item.impact_quantity_type}
                  />
                }
              />
            )}
            {item?.note && (
              <CollapsedItem
                title={t('scan-note-input-label.v')}
                node={<Typography sx={{ fontSize: '14px' }}>{item.note}</Typography>}
              />
            )}
            {item?.media && item.media.length > 0 && (
              <CollapsedItem
                title={t('scan-media-input-title.v', { ns: ETranslationNs.Scans })}
                node={<MediaList media={item.media} />}
              />
            )}
            <CollapsedItem
              title={t('within-geofence-label.v', { ns: ETranslationNs.Scans })}
              node={
                <Typography sx={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                  {item.within_geofence ? (
                    <Link
                      style={{ color: theme.palette.custom.primary }}
                      to="#"
                      onClick={(e) => {
                        e.preventDefault()
                        handleCenter()
                      }}
                    >
                      {t('scan-yes-label.v', { ns: ETranslationNs.Scans })}
                    </Link>
                  ) : (
                    t('scan-no-label.v', { ns: ETranslationNs.Scans })
                  )}
                </Typography>
              }
            />
            <ScanMap
              scannerLocation={scannerLocation}
              center={center}
              radius={item.step_radius}
              scannerName={item.scanner_name}
              stepName={item.step_name}
              stepLocations={stepLocations}
              mapRef={mapRef}
            />
            <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', my: '5px' }} />
            <CollapsedItem
              title={t('user-tx-hash-label.v', { ns: ETranslationNs.Scans })}
              node={
                userTxLoader || !item.user_tx ? (
                  <Loader size={18} isStatic />
                ) : (
                  <OuterLink
                    to={item.user_tx && getScanExplorerTxUrl(item.user_tx, item.network)}
                    text={userTransaction.txHash || item.user_tx || '-'}
                  />
                )
              }
            />
            <CollapsedItem
              title={t('account-tx-hash-label.v', { ns: ETranslationNs.Scans })}
              node={
                accountTxLoader ? (
                  <Loader size={18} isStatic />
                ) : (
                  <OuterLink
                    to={item.account_tx && getScanExplorerTxUrl(item.account_tx, item.network)}
                    text={accountTransaction.txHash || item.account_tx || '-'}
                  />
                )
              }
            />
            {(item?.reviewed_by ||
              item?.reviewed_at ||
              item?.pushed_at ||
              item?.pushed_by ||
              item?.not_approved_by ||
              item?.not_approved_on) && (
              <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', my: '5px' }} />
            )}
            {item?.reviewed_by && (
              <CollapsedItem
                title={t('scan-history-approved-by-lable.v', { ns: ETranslationNs.Scans })}
                node={
                  <Typography sx={{ color: theme.palette.custom.grey2, fontSize: '14px' }}>
                    {item.reviewed_by}
                  </Typography>
                }
              />
            )}
            {item?.reviewed_at && (
              <CollapsedItem
                title={t('table-reviewed-at-column.v', { ns: ETranslationNs.Scans })}
                node={
                  <Typography sx={{ color: theme.palette.custom.grey2, fontSize: '14px' }}>
                    {getTableTimestamp({ date: item.reviewed_at, timezone: userTz })}
                  </Typography>
                }
              />
            )}
            {item?.not_approved_by && (
              <CollapsedItem
                title={t('scan-not-approved-by-lable.v', { ns: ETranslationNs.Scans })}
                node={
                  <Typography sx={{ color: theme.palette.custom.grey2, fontSize: '14px' }}>
                    {item.not_approved_by}
                  </Typography>
                }
              />
            )}
            {item?.not_approved_on && (
              <CollapsedItem
                title={t('scan-not-approved-at-lable.v', { ns: ETranslationNs.Scans })}
                node={
                  <Typography sx={{ color: theme.palette.custom.grey2, fontSize: '14px' }}>
                    {getTableTimestamp({ date: item.not_approved_on, timezone: userTz })}
                  </Typography>
                }
              />
            )}
            {item?.pushed_by && (
              <CollapsedItem
                title={t('scan-history-pushed-by-lable.v', { ns: ETranslationNs.Scans })}
                node={
                  <Typography sx={{ color: theme.palette.custom.grey2, fontSize: '14px' }}>
                    {item.pushed_by}
                  </Typography>
                }
              />
            )}
            {item?.pushed_at && (
              <CollapsedItem
                title={t('scan-history-pushed-at-lable.v', { ns: ETranslationNs.Scans })}
                node={
                  <Typography sx={{ color: theme.palette.custom.grey2, fontSize: '14px' }}>
                    {getTableTimestamp({ date: item.pushed_at, timezone: userTz })}
                  </Typography>
                }
              />
            )}
          </ul>
        </Collapse>
      </TableCell>
    </TableRow>
  )
}
